// Schema.org JSON-LD generation and injection.
// Kept separate from the meta/head manager so that structured data
// and runtime OG/Twitter tag mutation stay decoupled.

import { getSiteUrl, resolveSeo, type SeoEntry } from './seo';

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'What is CSSBuy Spreadsheet?',
    a: 'CSSBuy Spreadsheet is an organized resource that helps users research products, categories, sellers and purchase information before buying through CSSBuy.',
  },
  {
    q: 'How to use CSSBuy Spreadsheet?',
    a: 'Start by searching for a product or category, compare information, verify seller details, check QC photos and understand shipping costs before proceeding.',
  },
  {
    q: 'Is CSSBuy legit?',
    a: 'CSSBuy is a known shopping agent service. This website provides independent guides and resources to help users make informed decisions.',
  },
  {
    q: 'Is CSSBuy safe?',
    a: 'Safety depends on following best practices: verifying sellers, checking QC, understanding shipping and never sharing sensitive information.',
  },
  {
    q: 'How much does shipping cost?',
    a: 'Shipping costs vary by destination, weight, volume, method and other factors. Always check current rates before ordering.',
  },
  {
    q: 'How does the warehouse work?',
    a: 'The warehouse stores your items, performs inspections, takes QC photos and consolidates packages before shipping.',
  },
  {
    q: 'What does QC mean?',
    a: 'QC stands for Quality Control. It involves checking product photos, measurements and details before the item is shipped to you.',
  },
  {
    q: 'How to verify a seller?',
    a: 'Check seller ratings, reviews, transaction history and verify product details through multiple sources before purchasing.',
  },
];

function buildWebsiteSchema(entry: SeoEntry): Record<string, unknown> {
  const origin = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'CSSBuy Spreadsheet',
    url: `${origin}/`,
    description: entry.description,
    inLanguage: 'en',
  };
}

function buildArticleSchema(entry: SeoEntry): Record<string, unknown> {
  const origin = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: entry.title,
    description: entry.description,
    dateModified: '2026-08-30',
    inLanguage: 'en',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${origin}${entry.path}`,
    },
  };
}

function buildFaqSchema(entry: SeoEntry): Record<string, unknown> {
  const origin = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: 'en',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${origin}${entry.path}`,
    },
  };
}

function buildWebPageSchema(entry: SeoEntry): Record<string, unknown> {
  const origin = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: entry.title,
    description: entry.description,
    inLanguage: 'en',
    url: `${origin}${entry.path}`,
  };
}

function buildSchema(entry: SeoEntry): Record<string, unknown> | null {
  switch (entry.schemaType) {
    case 'website':
      return buildWebsiteSchema(entry);
    case 'article':
      return buildArticleSchema(entry);
    case 'faq':
      return buildFaqSchema(entry);
    case 'webpage':
      return buildWebPageSchema(entry);
    default:
      return null;
  }
}

export function applyJsonLd(pathname: string): void {
  const entry = resolveSeo(pathname);

  // Remove previously injected structured data to avoid stale scripts.
  document
    .querySelectorAll('script[data-seo-jsonld]')
    .forEach((el) => el.remove());

  if (!entry) {
    return;
  }

  const schema = buildSchema(entry);
  if (!schema) {
    return;
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-jsonld', 'true');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}